/**
 * Tile-based cache pro chart data. Klíčová abstrakce:
 *   tile = (topicId, resolution, tileStart-tileEnd) → pole `ChartPoint`s
 *
 * Cache je sdílená napříč všemi instancemi Graph2 (více chartů na stránce = jedna
 * cache). Při odhlášení se vyprázdní. LRU eviction nad 100 MB.
 *
 * Viz docs/plans/CHART-DATA-REFACTOR.md (Fáze 2).
 */

import { create } from "zustand";
import {
  APPROX_BYTES_PER_POINT,
  CHART_CACHE_MAX_BYTES,
  type ChartResolutionKey,
} from "@client/lib/chartBucketing";

export type ChartPoint = {
  ts: number;
  v: number | boolean | null;
  /** Jen pro bucketed/stat (FLOAT). */
  min?: number;
  max?: number;
  count?: number;
};

export type ChartTile = {
  topicId: string;
  resolution: ChartResolutionKey;
  tileStart: number;
  tileEnd: number;
  points: ChartPoint[];
  fetchedAt: number;
  /** Pro LRU: last time tile byl čten. */
  lastAccessedAt: number;
  /** Optional boundary values — server posílá per topic, klient uloží do tile spolu. */
  boundaryBefore?: { ts: number; v: number | boolean };
  boundaryAfter?: { ts: number; v: number | boolean };
};

interface ChartStore {
  tiles: Map<string, ChartTile>;
  inFlight: Map<string, AbortController>;
  approxBytes: number;

  putTile: (key: string, tile: ChartTile) => void;
  /** Označí access (LRU touch). */
  touchTile: (key: string) => void;
  /** Vrátí tile NEBO undefined — nezmění lastAccessedAt (read-only peek). */
  peekTile: (key: string) => ChartTile | undefined;

  setInFlight: (key: string, controller: AbortController) => void;
  clearInFlight: (key: string) => void;
  isInFlight: (key: string) => boolean;
  abortInFlight: (key: string) => void;
  abortAll: () => void;

  clear: () => void;
}

/** Hrubý odhad paměti per tile. */
const tileBytes = (tile: ChartTile): number => tile.points.length * APPROX_BYTES_PER_POINT;

export const useChartStore = create<ChartStore>((set, get) => ({
  tiles: new Map(),
  inFlight: new Map(),
  approxBytes: 0,

  putTile: (key, tile) =>
    set((state) => {
      const next = new Map(state.tiles);
      const oldBytes = state.tiles.get(key) ? tileBytes(state.tiles.get(key)!) : 0;
      next.set(key, tile);
      let newApproxBytes = state.approxBytes - oldBytes + tileBytes(tile);

      // LRU eviction pokud překračujeme soft cap
      if (newApproxBytes > CHART_CACHE_MAX_BYTES) {
        const sorted = [...next.entries()].sort(([, a], [, b]) => a.lastAccessedAt - b.lastAccessedAt);
        while (newApproxBytes > CHART_CACHE_MAX_BYTES * 0.8 && sorted.length > 1) {
          const [evictKey, evictTile] = sorted.shift()!;
          if (evictKey === key) continue; // never evict tile we just wrote
          next.delete(evictKey);
          newApproxBytes -= tileBytes(evictTile);
        }
      }

      return { tiles: next, approxBytes: newApproxBytes };
    }),

  touchTile: (key) =>
    set((state) => {
      const tile = state.tiles.get(key);
      if (!tile) return state;
      const next = new Map(state.tiles);
      next.set(key, { ...tile, lastAccessedAt: Date.now() });
      return { tiles: next };
    }),

  peekTile: (key) => get().tiles.get(key),

  setInFlight: (key, controller) =>
    set((state) => {
      const next = new Map(state.inFlight);
      next.set(key, controller);
      return { inFlight: next };
    }),

  clearInFlight: (key) =>
    set((state) => {
      if (!state.inFlight.has(key)) return state;
      const next = new Map(state.inFlight);
      next.delete(key);
      return { inFlight: next };
    }),

  isInFlight: (key) => get().inFlight.has(key),

  abortInFlight: (key) => {
    const controller = get().inFlight.get(key);
    if (controller) controller.abort();
    get().clearInFlight(key);
  },

  abortAll: () => {
    for (const controller of get().inFlight.values()) controller.abort();
    set({ inFlight: new Map() });
  },

  clear: () => {
    for (const controller of get().inFlight.values()) controller.abort();
    set({ tiles: new Map(), inFlight: new Map(), approxBytes: 0 });
  },
}));
